import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import enResources from './locales/en';

// Available languages for the selector
export const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
];

// Lazy-loaded locale chunks (each becomes its own JS chunk)
// Only English is bundled with the main app; others load on demand.
const lazyLoaders: Record<string, () => Promise<{ default: typeof enResources }>> = {
  es: () => import('./locales/es'),
  fr: () => import('./locales/fr'),
  de: () => import('./locales/de'),
  ja: () => import('./locales/ja'),
};

const loadedLanguages = new Set<string>(['en']);

/**
 * Dynamically load a language bundle and add it to i18next.
 * Safe to call multiple times — only fetches once per language.
 */
export async function loadLanguage(lng: string): Promise<void> {
  if (loadedLanguages.has(lng)) return;
  const loader = lazyLoaders[lng];
  if (!loader) return; // Unknown lang — fallback to English
  try {
    const mod = await loader();
    i18n.addResourceBundle(lng, 'translation', mod.default.translation, true, true);
    loadedLanguages.add(lng);
  } catch {
    // Network failure — fall back silently to English
  }
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: { en: enResources },
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false, // React already escapes by default
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },
  });

// On init, if the detected/persisted language is not English, lazy-load it
// then re-trigger a language change so React subtrees re-render with the new strings.
const initialLng = (i18n.language || 'en').split('-')[0];
if (initialLng !== 'en' && lazyLoaders[initialLng]) {
  loadLanguage(initialLng).then(() => {
    i18n.changeLanguage(initialLng);
  });
}

// Whenever the user switches language at runtime, ensure its chunk is loaded.
i18n.on('languageChanged', (lng) => {
  const base = lng.split('-')[0];
  if (!loadedLanguages.has(base) && lazyLoaders[base]) {
    loadLanguage(base);
  }
});

export default i18n;
